import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import { toast } from "react-toastify";

// ─── Barcode library & beep ───────────────────────────────
import { BrowserMultiFormatReader, BrowserCodeReader } from "@zxing/browser";
import JsBarcode from "jsbarcode";
import beepSfx from "assets/beep.mp3";

// ─── Register components & context ─────────────────────────
import { useRegister } from "./RegisterContext";
import RegisterDashboard from "./RegisterDashboard";
import OpenRegisterModal from "./OpenRegisterModal";
import CloseRegisterModal from "./CloseRegisterModal";
import WithdrawalModal from "./WithdrawalModal";
import WithdrawalBanner from "./WithdrawalBanner";

/* ── Icons ───────────────────────────────────────────────── */
const Icon = ({ d, size = 15, stroke = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const IconSearch   = () => <Icon d={["M11 3a8 8 0 1 0 0 16A8 8 0 0 0 11 3z","M21 21l-4.35-4.35"]} />;
const IconCart     = () => <Icon size={32} stroke={1.4} d={["M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z","M3 6h18","M16 10a4 4 0 0 1-8 0"]} />;
const IconPlus     = () => <Icon d={["M12 5v14","M5 12h14"]} />;
const IconMinus    = () => <Icon d="M5 12h14" />;
const IconCheck    = () => <Icon d="M20 6 9 17 4 12" stroke={2.5} />;
const IconReset    = () => <Icon d={["M1 4v6h6","M3.51 15a9 9 0 1 0 2.13-9.36L1 10"]} />;
const IconMaximize = () => <Icon d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />;
const IconMinimize = () => <Icon d={["M4 14h6v6","M20 10h-6V4","M10 20l-7-7","M21 3l-7 7"]} />;
const IconPause    = () => <Icon d={["M6 4h4v16H6z","M14 4h4v16h-4z"]} />;
const IconFile     = () => <Icon d={["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z","M14 2v6h6"]} />;
const IconChevron  = () => <Icon d="M6 9l6 6 6-9" size={11} stroke={2.5} />;
const IconBarcode  = () => <Icon d={["M4 7V4h3","M17 4h3v3","M4 17v3h3","M17 20h3v-3","M7 8v8","M10 8v8","M13 8v8","M16 8v8"]} />;
const IconUser     = () => <Icon d={["M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2","M12 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"]} />;
const IconX        = () => <Icon d="M18 6 6 18M6 6l12 12" size={18} stroke={2} />;
const IconCamera   = () => <Icon size={20} d={["M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z","M12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"]} />;
const IconPrinter  = () => <Icon size={16} d={["M6 9V2h12v7","M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2","M6 14h12v8H6z"]} />;

const BRANDS     = ["All Brands", "Apple", "Sony", "Samsung", "Nikon", "Ray-Ban"];
const WAREHOUSES = ["Warehouse 1", "Warehouse 2", "Main Store"];
const fmt = (n) => `${Number(n || 0).toFixed(3)} TND`;

/* ── CSS (existing POS CSS + register CSS) ────────────── */
const REGISTER_CSS = `
  /* ── Enhanced Register Modals ───────────────── */
  .pos-register-modal {
    background: #151827;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 20px;
    width: 420px;
    max-width: 95vw;
    box-shadow: 0 32px 80px rgba(0,0,0,0.7);
    overflow: hidden;
  }

  .pos-register-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 24px 14px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .pos-register-header__icon {
    width: 40px; height: 40px;
    border-radius: 12px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    display: flex; align-items: center; justify-content: center;
    color: #fff;
  }
  .pos-register-header__title {
    font-size: 16px; font-weight: 700; color: #fff;
    letter-spacing: -0.01em;
  }
  .pos-register-header__status {
    font-size: 11px; color: rgba(255,255,255,0.4);
  }

  .pos-register-body {
    padding: 18px 24px;
    display: flex; flex-direction: column; gap: 14px;
  }

  .pos-register-field {
    display: flex; flex-direction: column; gap: 4px;
  }
  .pos-register-field label {
    font-size: 10px; font-weight: 600; letter-spacing: 0.07em;
    color: rgba(255,255,255,0.3); text-transform: uppercase;
  }
  .pos-register-input {
    padding: 10px 14px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    background: rgba(255,255,255,0.05);
    color: #e2e4f0; font-size: 14px;
    outline: none;
    transition: border-color 0.2s;
  }
  .pos-register-input:focus {
    border-color: #6366f1;
  }
  .pos-register-input::placeholder {
    color: rgba(255,255,255,0.2);
  }
  .pos-register-note {
    font-size: 11px; color: rgba(255,255,255,0.35);
    line-height: 1.5;
    background: rgba(99,102,241,0.06);
    border-left: 3px solid #6366f1;
    padding: 8px 12px;
    border-radius: 6px;
  }

  .pos-register-actions {
    display: flex; gap: 10px;
    padding: 16px 24px 20px;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
  .pos-register-cancel {
    flex: 1;
    padding: 10px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.5);
    font-size: 13px; cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
  }
  .pos-register-cancel:hover {
    border-color: rgba(255,255,255,0.2);
    color: #fff;
  }
  .pos-register-confirm {
    flex: 1;
    padding: 10px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #fff; font-size: 13px; font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
    display: flex; align-items: center; justify-content: center; gap: 6px;
  }
  .pos-register-confirm:disabled {
    opacity: 0.5; cursor: not-allowed;
  }
  .pos-register-confirm--danger {
    background: linear-gradient(135deg, #ff4757, #e84118);
  }

  /* ── Withdrawal Modal specific ── */
  .pos-withdrawal-balance {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 8px;
    display: flex; justify-content: space-between;
    align-items: center;
  }
  .pos-withdrawal-balance span:first-child {
    color: rgba(255,255,255,0.5); font-size: 12px;
  }
  .pos-withdrawal-balance strong {
    color: #8b8ff7; font-size: 16px;
  }

  /* ── Dashboard Balance Widget ── */
  .pos-dashboard-balance {
    background: linear-gradient(135deg, #1a1d2e, #252a3e);
    border: 1px solid rgba(99,102,241,0.3);
    border-radius: 12px;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 8px 16px;
  }
  .pos-dashboard-balance__icon {
    width: 36px; height: 36px;
    border-radius: 8px;
    background: rgba(99,102,241,0.12);
    display: flex; align-items: center; justify-content: center;
    color: #8b8ff7;
  }
  .pos-dashboard-balance__text {
    font-size: 13px; color: rgba(255,255,255,0.5);
  }
  .pos-dashboard-balance__value {
    font-size: 20px; font-weight: 700; color: #8b8ff7;
  }
`;

/* ── Receipt / Invoice CSS ──────────────────────────────── */
const RECEIPT_CSS = `
  .pos-receipt-modal { width: 380px; }
  .pos-receipt-header {
    display: flex; align-items: center; gap: 10px;
    margin-bottom: 14px;
  }
  .pos-receipt-logo {
    width: 36px; height: 36px; border-radius: 10px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #fff; font-weight: 800; font-size: 15px;
    display: flex; align-items: center; justify-content: center;
  }
  .pos-receipt-brand { font-weight: 700; font-size: 16px; color: #fff; letter-spacing: -0.01em; }
  .pos-receipt-meta {
    font-size: 12px; color: rgba(255,255,255,0.55);
    display: flex; flex-direction: column; gap: 3px;
    margin-bottom: 14px; padding-bottom: 12px;
    border-bottom: 1px dashed rgba(255,255,255,0.12);
  }
  .pos-receipt-items { margin-bottom: 14px; }
  .pos-receipt-item-row {
    display: grid; grid-template-columns: 1.4fr 1fr 0.7fr;
    gap: 8px; font-size: 12px; color: #e2e4f0; margin-bottom: 5px;
  }
  .pos-receipt-item-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .pos-receipt-item-qty { color: rgba(255,255,255,0.45); text-align: right; }
  .pos-receipt-item-total { text-align: right; font-weight: 600; }
  .pos-receipt-totals {
    font-size: 12px; padding-top: 10px;
    border-top: 1px dashed rgba(255,255,255,0.12);
    display: flex; flex-direction: column; gap: 5px;
  }
  .pos-receipt-totals > div { display: flex; justify-content: space-between; color: rgba(255,255,255,0.55); }
  .pos-receipt-total-grand { font-weight: 700 !important; font-size: 14px; color: #fff !important; margin-top: 4px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.1); }
  .pos-receipt-total-grand span:last-child { color: #8b8ff7; }
  .pos-receipt-thanks {
    text-align: center; font-size: 12px; color: rgba(255,255,255,0.5);
    margin: 16px 0 10px; line-height: 1.5;
  }
  .pos-receipt-barcode {
    display: block;
    margin: 0 auto;
    background: #fff;
    border-radius: 6px;
    padding: 4px;
    max-width: 200px;
  }

  /* ── Print: only the receipt content ─────────────────── */
  @media print {
    body * { visibility: hidden !important; }
    .pos-receipt-printable, .pos-receipt-printable * { visibility: visible !important; }
    .pos-receipt-printable {
      position: absolute; top: 0; left: 0; width: 100%;
      background: #fff; color: #111; padding: 20px;
    }
    .pos-receipt-brand, .pos-receipt-item-total,
    .pos-receipt-total-grand span:last-child { color: #111 !important; }
    .pos-receipt-meta, .pos-receipt-item-qty,
    .pos-receipt-totals > div, .pos-receipt-thanks { color: #444 !important; }
    .pos-receipt-noprint { display: none !important; }
  }
`;


const CSS = `
  .pos-root {
    display: flex; flex-direction: column; height: 100vh;
    font-family: 'DM Sans', system-ui, sans-serif;
    font-size: 13px;
    background: #0d0f18;
    color: #e2e4f0;
  }

  /* ── Topbar ── */
  .pos-topbar {
    display: flex; align-items: center; gap: 8px;
    padding: 0 16px; height: 52px; flex-shrink: 0;
    background: #111420;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    box-shadow: 0 1px 12px rgba(0,0,0,0.4);
  }
  .pos-topbar__brand {
    display: flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 8px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #fff; font-weight: 800; font-size: 14px;
    box-shadow: 0 0 14px rgba(99,102,241,0.4);
    flex-shrink: 0; margin-right: 8px;
  }
  .pos-topbar__divider {
    width: 1px; height: 20px;
    background: rgba(255,255,255,0.08); margin: 0 6px;
  }
  .pos-topbar__icon-btn {
    width: 30px; height: 30px; border: none; border-radius: 6px;
    background: transparent; cursor: pointer;
    color: rgba(255,255,255,0.45);
    display: flex; align-items: center; justify-content: center;
    transition: background 0.15s, color 0.15s;
  }
  .pos-topbar__icon-btn:hover { background: rgba(255,255,255,0.07); color: #fff; }
  .pos-topbar__avatar {
    width: 30px; height: 30px; border-radius: 50%;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #fff; font-weight: 700; font-size: 13px;
    display: flex; align-items: center; justify-content: center;
    margin-left: 4px;
  }
  .pos-register-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 5px 12px; border: 1px solid rgba(255,255,255,0.1);
    border-radius: 6px; background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.6); font-size: 12px; cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .pos-register-btn:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.2); color: #fff; }
  .pos-status-dot {
    width: 7px; height: 7px; border-radius: 50%; background: #ff4757;
  }

  /* ── Dark Select ── */
  .pos-select-wrap { position: relative; }
  .pos-select {
    appearance: none; outline: none; cursor: pointer;
    padding: 5px 28px 5px 10px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 6px;
    background: rgba(255,255,255,0.05);
    color: rgba(255,255,255,0.7); font-size: 12px;
    transition: border-color 0.15s, background 0.15s;
  }
  .pos-select:hover { border-color: rgba(99,102,241,0.5); background: rgba(99,102,241,0.08); }
  .pos-select option { background: #1a1d2e; color: #e2e4f0; }
  .pos-select-chevron {
    position: absolute; right: 8px; top: 50%;
    transform: translateY(-50%); pointer-events: none;
    color: rgba(255,255,255,0.35); display: flex; align-items: center;
  }

  /* ── Main layout ── */
  .pos-main { display: flex; flex: 1; overflow: hidden; }

  /* ── Left: Checkout panel ── */
  .pos-checkout {
    width: 360px; min-width: 320px; flex-shrink: 0;
    background: #111420;
    border-right: 1px solid rgba(255,255,255,0.06);
    display: flex; flex-direction: column;
  }
  .pos-checkout__header {
    padding: 14px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    font-weight: 700; font-size: 15px; color: #fff;
    letter-spacing: -0.01em;
    display: flex; align-items: center; justify-content: space-between;
  }
  .pos-checkout__count {
    font-size: 11px; font-weight: 600; padding: 2px 8px;
    border-radius: 20px; background: rgba(99,102,241,0.18);
    color: #8b8ff7; letter-spacing: 0;
  }

  /* Cart items */
  .pos-cart { flex: 1; overflow-y: auto; padding: 6px 16px; }
  .pos-cart::-webkit-scrollbar { width: 4px; }
  .pos-cart::-webkit-scrollbar-track { background: transparent; }
  .pos-cart::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
  .pos-cart__empty {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    height: 150px; color: rgba(255,255,255,0.2); gap: 8px;
  }
  .pos-cart__empty-label { font-size: 13px; }
  .pos-cart__empty-sub { font-size: 11px; color: rgba(255,255,255,0.15); }
  .pos-cart__item {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 0;
    border-bottom: 1px solid rgba(255,255,255,0.05);
  }
  .pos-cart__img {
    width: 38px; height: 38px; object-fit: cover;
    border-radius: 8px; flex-shrink: 0;
    background: rgba(255,255,255,0.05);
  }
  .pos-cart__name {
    font-weight: 500; font-size: 12px; line-height: 1.3;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    color: #e2e4f0;
  }
  .pos-cart__price { color: #8b8ff7; font-size: 12px; margin-top: 2px; }
  .pos-qty-btn {
    width: 22px; height: 22px; border: 1px solid rgba(255,255,255,0.12);
    border-radius: 5px; background: rgba(255,255,255,0.05);
    cursor: pointer; color: rgba(255,255,255,0.6);
    display: flex; align-items: center; justify-content: center;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
  }
  .pos-qty-btn:hover { border-color: #6366f1; background: rgba(99,102,241,0.15); color: #fff; }
  .pos-qty-val { width: 24px; text-align: center; font-size: 13px; font-weight: 600; color: #fff; }
  .pos-cart__total { width: 60px; text-align: right; font-size: 12px; font-weight: 600; color: #e2e4f0; }
  .pos-remove-btn {
    border: none; background: transparent; cursor: pointer;
    color: rgba(255,255,255,0.2); font-size: 18px; padding: 0; line-height: 1;
    transition: color 0.15s;
  }
  .pos-remove-btn:hover { color: #ff4757; }

  /* Inputs section */
  .pos-inputs {
    padding: 12px 16px;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
  .pos-inputs__row { display: flex; gap: 8px; margin-bottom: 8px; }
  .pos-field-label {
    display: block; font-size: 10px; font-weight: 600;
    letter-spacing: 0.07em; color: rgba(255,255,255,0.3);
    text-transform: uppercase; margin-bottom: 3px;
  }
  .pos-field-wrap {
    display: flex; border: 1px solid rgba(255,255,255,0.1);
    border-radius: 7px; overflow: hidden;
    background: rgba(255,255,255,0.04);
    transition: border-color 0.15s;
  }
  .pos-field-wrap:focus-within { border-color: rgba(99,102,241,0.5); }
  .pos-field-input {
    flex: 1; border: none; outline: none; background: transparent;
    padding: 6px 8px; font-size: 12px; color: #e2e4f0; width: 100%;
  }
  .pos-field-suffix {
    padding: 6px 9px;
    background: rgba(255,255,255,0.03);
    border-left: 1px solid rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.3); font-size: 11px; font-weight: 600;
    display: flex; align-items: center;
  }
  .pos-points-bar {
    display: flex; align-items: center; gap: 8px;
    padding: 8px 11px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 7px; margin-bottom: 7px;
  }
  .pos-points-label { font-size: 12px; color: rgba(255,255,255,0.4); flex: 1; }
  .pos-convert-btn {
    padding: 5px 14px; border: 1px solid rgba(255,255,255,0.1);
    border-radius: 6px; background: rgba(255,255,255,0.05);
    color: rgba(255,255,255,0.5); font-size: 12px; cursor: pointer;
    display: flex; align-items: center; gap: 5px;
    transition: border-color 0.15s, color 0.15s;
  }
  .pos-convert-btn:hover { border-color: #6366f1; color: #8b8ff7; }

  /* Totals */
  .pos-totals {
    padding: 12px 16px;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
  .pos-totals__row {
    display: flex; justify-content: space-between;
    margin-bottom: 5px; font-size: 12px;
  }
  .pos-totals__label { color: rgba(255,255,255,0.35); }
  .pos-totals__val { color: #e2e4f0; }
  .pos-totals__val--discount { color: #ff4757; font-weight: 600; }
  .pos-totals__grand {
    display: flex; justify-content: space-between;
    margin-top: 10px; padding-top: 10px;
    border-top: 1px solid rgba(255,255,255,0.08);
    font-size: 14px; font-weight: 700;
  }
  .pos-totals__grand-label { color: #fff; }
  .pos-totals__grand-val { color: #8b8ff7; }

  /* ── Right: Products ── */
  .pos-products { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
  .pos-search-bar {
    padding: 10px 16px;
    background: #111420;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    display: flex; align-items: center; gap: 8px; flex-shrink: 0;
  }
  .pos-search-wrap {
    flex: 1; display: flex; align-items: center;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 9px; padding: 7px 12px;
    background: rgba(255,255,255,0.04);
    transition: border-color 0.15s;
  }
  .pos-search-wrap:focus-within { border-color: rgba(99,102,241,0.5); }
  .pos-search-icon { color: rgba(255,255,255,0.3); margin-right: 8px; display: flex; align-items: center; }
  .pos-search-input {
    border: none; background: transparent; outline: none;
    flex: 1; font-size: 13px; color: #e2e4f0;
  }
  .pos-search-input::placeholder { color: rgba(255,255,255,0.25); }
  .pos-scan-btn {
    width: 36px; height: 36px; border: 1px solid rgba(255,255,255,0.1);
    border-radius: 8px; background: rgba(255,255,255,0.04);
    cursor: pointer; color: rgba(255,255,255,0.45);
    display: flex; align-items: center; justify-content: center;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .pos-scan-btn:hover { border-color: #6366f1; color: #8b8ff7; background: rgba(99,102,241,0.1); }
  .pos-scan-btn--active {
    border-color: #6366f1 !important;
    color: #8b8ff7 !important;
    background: rgba(99,102,241,0.18) !important;
    box-shadow: 0 0 0 2px rgba(99,102,241,0.25);
  }

  /* Product grid */
  .pos-grid-wrap { flex: 1; overflow-y: auto; padding: 14px; background: #0d0f18; }
  .pos-grid-wrap::-webkit-scrollbar { width: 4px; }
  .pos-grid-wrap::-webkit-scrollbar-track { background: transparent; }
  .pos-grid-wrap::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 4px; }
  .pos-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
    gap: 12px;
  }
  .pos-empty { text-align: center; padding: 60px 0; color: rgba(255,255,255,0.2); }

  /* Product card */
  .pos-card {
    background: #111420;
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 14px; overflow: hidden; cursor: pointer;
    transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s;
  }
  .pos-card:hover {
    border-color: rgba(99,102,241,0.45);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.35);
  }
  .pos-card__img-wrap {
    height: 105px; overflow: hidden;
    background: rgba(255,255,255,0.04);
    display: flex; align-items: center; justify-content: center;
  }
  .pos-card__img { width: 100%; height: 100%; object-fit: cover; }
  .pos-card__body { padding: 9px 10px 10px; }
  .pos-card__name {
    font-weight: 500; font-size: 12px; line-height: 1.35;
    color: #e2e4f0; min-height: 32px;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .pos-card__sku { font-size: 10px; color: rgba(255,255,255,0.25); margin: 3px 0 6px; }
  .pos-card__footer { display: flex; align-items: center; justify-content: space-between; }
  .pos-card__price { font-weight: 700; font-size: 13px; color: #fff; }
  .pos-add-btn {
    width: 28px; height: 28px; border-radius: 50%; border: none;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #fff; font-size: 18px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 12px rgba(99,102,241,0.4);
    transition: transform 0.15s, box-shadow 0.15s;
    line-height: 1;
  }
  .pos-add-btn:hover { transform: scale(1.1); box-shadow: 0 6px 18px rgba(99,102,241,0.55); }

  /* Pagination */
  .pos-pagination {
    display: flex; justify-content: center; align-items: center;
    gap: 6px; margin-top: 18px; padding-bottom: 6px;
  }
  .pos-page-btn {
    width: 28px; height: 28px;
    border: 1px solid rgba(255,255,255,0.1); border-radius: 6px;
    background: transparent; color: rgba(255,255,255,0.5);
    cursor: pointer; font-size: 12px; font-weight: 500;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
  }
  .pos-page-btn:hover:not([disabled]) { border-color: #6366f1; color: #8b8ff7; }
  .pos-page-btn--active { background: #6366f1; border-color: #6366f1; color: #fff; }
  .pos-page-btn[disabled] { opacity: 0.3; cursor: not-allowed; }
  .pos-page-info { font-size: 11px; color: rgba(255,255,255,0.3); }

  /* ── Bottom bar ── */
  .pos-bottombar {
    display: flex; align-items: center;
    background: #111420;
    border-top: 1px solid rgba(255,255,255,0.06);
    padding: 0 16px; height: 52px; flex-shrink: 0;
    gap: 2px;
  }
  .pos-online {
    display: flex; align-items: center; gap: 6px; margin-right: 20px;
  }
  .pos-online__dot { width: 7px; height: 7px; border-radius: 50%; background: #2ed573; }
  .pos-online__label { font-size: 12px; color: #2ed573; font-weight: 500; }
  .pos-bottom-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 14px; border: none; border-radius: 7px;
    background: transparent; font-size: 12px; font-weight: 500;
    color: rgba(255,255,255,0.45); cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }
  .pos-bottom-btn:hover { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.85); }
  .pos-total-block { text-align: right; margin-right: 16px; }
  .pos-total-block__label {
    font-size: 10px; color: rgba(255,255,255,0.3);
    text-transform: uppercase; letter-spacing: 0.07em;
  }
  .pos-total-block__amount { font-weight: 700; font-size: 17px; color: #8b8ff7; }
  .pos-pay-btn {
    padding: 10px 28px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #fff; border: none; border-radius: 10px;
    font-size: 13px; font-weight: 700; letter-spacing: 0.03em;
    cursor: pointer; display: flex; align-items: center; gap: 7px;
    box-shadow: 0 6px 20px rgba(99,102,241,0.4);
    transition: transform 0.15s, box-shadow 0.15s, opacity 0.15s;
  }
  .pos-pay-btn:hover:not([disabled]) { transform: translateY(-1px); box-shadow: 0 10px 28px rgba(99,102,241,0.55); }
  .pos-pay-btn[disabled] { opacity: 0.4; cursor: not-allowed; box-shadow: none; }

  /* ── Modal ── */
  .pos-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.65);
    display: flex; align-items: center; justify-content: center; z-index: 200;
    backdrop-filter: blur(4px);
  }
  .pos-modal {
    background: #151827;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 16px; padding: 28px; width: 360px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.6);
  }
  .pos-modal h3 {
    margin: 0 0 20px; font-size: 16px; font-weight: 700; color: #fff; letter-spacing: -0.01em;
  }
  .pos-modal__summary {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 10px; padding: 12px 16px; margin-bottom: 20px;
  }
  .pos-modal__summary-row {
    display: flex; justify-content: space-between;
    font-size: 13px; margin-bottom: 5px;
  }
  .pos-modal__summary-row:last-child { margin-bottom: 0; }
  .pos-modal__summary-label { color: rgba(255,255,255,0.4); }
  .pos-modal__summary-val { font-weight: 600; color: #8b8ff7; }
  .pos-modal__summary-val--neutral { color: #e2e4f0; }
  .pos-method-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
  .pos-method-btn {
    padding: 12px 8px; border: 1px solid rgba(255,255,255,0.1);
    border-radius: 9px; background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.6); font-size: 13px; font-weight: 500;
    cursor: pointer; transition: all 0.15s;
  }
  .pos-method-btn:hover { border-color: #6366f1; background: rgba(99,102,241,0.12); color: #fff; }
  .pos-modal__actions { display: flex; gap: 10px; }
  .pos-modal__cancel {
    flex: 1; padding: 10px; border: 1px solid rgba(255,255,255,0.1);
    border-radius: 8px; background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.5); font-size: 13px; cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
  }
  .pos-modal__cancel:hover { border-color: rgba(255,255,255,0.2); color: #fff; }
  .pos-modal__confirm {
    flex: 1; padding: 10px; border: none;
    border-radius: 8px; background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #fff; font-size: 13px; font-weight: 600; cursor: pointer;
    transition: opacity 0.15s;
  }
  .pos-modal__confirm:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── Camera Scanner Modal ── */
  .pos-camera-modal {
    background: #151827;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 20px;
    width: 420px; max-width: 95vw;
    box-shadow: 0 32px 80px rgba(0,0,0,0.7);
    overflow: hidden;
  }
  .pos-camera-modal__header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 20px 14px;
  }
  .pos-camera-modal__title {
    font-size: 15px; font-weight: 700; color: #fff; letter-spacing: -0.01em;
  }
  .pos-camera-modal__close {
    width: 30px; height: 30px; border: none;
    border-radius: 8px; background: rgba(255,255,255,0.06);
    cursor: pointer; color: rgba(255,255,255,0.5);
    display: flex; align-items: center; justify-content: center;
    transition: background 0.15s, color 0.15s;
  }
  .pos-camera-modal__close:hover { background: rgba(255,255,255,0.12); color: #fff; }
  .pos-camera-viewport {
    position: relative; width: 100%; aspect-ratio: 4/3;
    background: #000; overflow: hidden;
  }
  .pos-camera-viewport video {
    width: 100%; height: 100%; object-fit: cover; display: block;
  }
  .pos-scan-overlay {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    pointer-events: none;
  }
  .pos-scan-frame {
    width: 220px; height: 140px; position: relative;
  }
  .pos-scan-frame::before, .pos-scan-frame::after,
  .pos-scan-frame__inner::before, .pos-scan-frame__inner::after {
    content: ''; position: absolute; width: 28px; height: 28px;
    border-color: #6366f1; border-style: solid;
  }
  .pos-scan-frame::before  { top: 0;    left: 0;  border-width: 3px 0 0 3px; border-radius: 6px 0 0 0; }
  .pos-scan-frame::after   { top: 0;    right: 0; border-width: 3px 3px 0 0; border-radius: 0 6px 0 0; }
  .pos-scan-frame__inner::before { bottom: 0; left: 0;  border-width: 0 0 3px 3px; border-radius: 0 0 0 6px; }
  .pos-scan-frame__inner::after  { bottom: 0; right: 0; border-width: 0 3px 3px 0; border-radius: 0 0 6px 0; }
  .pos-scan-laser {
    position: absolute; left: 10px; right: 10px; height: 2px;
    background: linear-gradient(90deg, transparent, #6366f1, #8b5cf6, transparent);
    border-radius: 2px;
    animation: scanLaser 1.8s ease-in-out infinite;
    box-shadow: 0 0 8px rgba(99,102,241,0.8);
  }
  @keyframes scanLaser {
    0%   { top: 12px; opacity: 0; }
    10%  { opacity: 1; }
    90%  { opacity: 1; }
    100% { top: calc(100% - 14px); opacity: 0; }
  }
  .pos-scan-success-flash {
    position: absolute; inset: 0;
    background: rgba(99,102,241,0.25);
    animation: flashIn 0.35s ease forwards;
  }
  @keyframes flashIn {
    0%   { opacity: 1; }
    100% { opacity: 0; }
  }
  .pos-camera-modal__footer {
    padding: 14px 20px 18px;
    display: flex; flex-direction: column; gap: 10px;
  }
  .pos-camera-status {
    display: flex; align-items: center; gap: 8px;
    font-size: 12px; color: rgba(255,255,255,0.4);
  }
  .pos-camera-status__dot {
    width: 6px; height: 6px; border-radius: 50%; background: #2ed573;
    animation: pulse 1.5s ease infinite;
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.8); }
  }
  .pos-camera-status__dot--idle { background: rgba(255,255,255,0.2); animation: none; }
  .pos-camera-result {
    display: flex; align-items: center; gap: 8px;
    padding: 9px 12px;
    background: rgba(99,102,241,0.1);
    border: 1px solid rgba(99,102,241,0.3);
    border-radius: 8px; font-size: 12px;
  }
  .pos-camera-result__code { flex: 1; font-family: monospace; color: #8b8ff7; font-size: 13px; }
  .pos-camera-select {
    width: 100%;
    appearance: none; outline: none; cursor: pointer;
    padding: 7px 10px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 7px;
    background: rgba(255,255,255,0.05);
    color: rgba(255,255,255,0.7); font-size: 12px;
  }
  .pos-camera-select option { background: #1a1d2e; color: #e2e4f0; }

  /* ── Register CSS ──────────────────────────────────── */
  ${REGISTER_CSS}

  /* ── Receipt CSS ───────────────────────────────────── */
  ${RECEIPT_CSS}

  /* ── Drafts Modal ──────────────────────────────────── */
  .pos-drafts-modal { width: 400px; max-width: 95vw; }
  .pos-drafts-list {
    max-height: 340px; overflow-y: auto; margin-bottom: 10px;
  }
  .pos-drafts-list::-webkit-scrollbar { width: 4px; }
  .pos-drafts-list::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
  .pos-draft-card {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px;
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 10px;
    margin-bottom: 8px;
    background: rgba(255,255,255,0.04);
  }
  .pos-draft-card__info { flex: 1; min-width: 0; }
  .pos-draft-card__meta {
    font-size: 11px; color: rgba(255,255,255,0.4); margin-bottom: 4px;
  }
  .pos-draft-card__items {
    font-size: 13px; font-weight: 600; color: #e2e4f0;
  }
  .pos-draft-card__customer {
    font-size: 11px; color: #8b8ff7; margin-top: 2px;
  }
  .pos-draft-card__actions {
    display: flex; gap: 6px; flex-shrink: 0;
  }
  .pos-draft-card__actions .pos-modal__confirm,
  .pos-draft-card__actions .pos-modal__cancel {
    padding: 6px 12px; font-size: 11px; border-radius: 6px; margin: 0;
  }
`;

export default function POS() {
  const axiosPrivate = useAxiosPrivate();
  const { user } = useCurrentUser();
  const {
    session,
    hasOpenSession,
    setOpenModalVisible,
    setCloseModalVisible,
  } = useRegister();

  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All Categories");
  const [brand, setBrand] = useState("All Brands");
  const [warehouse, setWarehouse] = useState("Warehouse 1");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [taxPct, setTaxPct] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [shipping, setShipping] = useState(0);
  const [points, setPoints] = useState(0);
  const [page, setPage] = useState(1);
  const [payModal, setPayModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [amountTendered, setAmountTendered] = useState("");
  const PER_PAGE = 12;
  const searchRef = useRef();

  // ── Receipt / invoice state ─────────────────────────────
  const [receiptData, setReceiptData] = useState(null);
  const barcodeRef = useRef(null);

  // ── Customer states ────────────────────────────────────
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [newCustomerModal, setNewCustomerModal] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({ name: "", phone: "", email: "" });
  const customerRef = useRef();

  // ── Drafts state ────────────────────────────────────────
  const [drafts, setDrafts] = useState([]);
  const [draftsModalVisible, setDraftsModalVisible] = useState(false);

  // ── Beep ──────────────────────────────────────────────
  const beepRef = useRef(null);
  useEffect(() => {
    beepRef.current = new Audio(beepSfx);
    beepRef.current.preload = "auto";
  }, []);

  const playBeep = useCallback(() => {
    if (beepRef.current) {
      beepRef.current.currentTime = 0;
      beepRef.current.play().catch(() => { });
    }
  }, []);

  // ── Render barcode on the receipt whenever it changes ──
  useEffect(() => {
    if (receiptData && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, receiptData.reference, {
          format: "CODE128",
          width: 1.2,
          height: 26,
          displayValue: true,
          fontSize: 9,
          margin: 0,
          background: "transparent",
          lineColor: "#1a1a1a",
        });
      } catch (e) {
        console.warn("Barcode render failed", e);
      }
    }
  }, [receiptData]);

  // ── Camera scanner state ───────────────────────────────
  const [cameraModal, setCameraModal] = useState(false);
  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [scanResult, setScanResult] = useState("");
  const [scanFlash, setScanFlash] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);

  // Load ZXing lazily when camera modal opens
  useEffect(() => {
    if (!cameraModal) return;
    let cancelled = false;
    (async () => {
      try {
        const devices = await BrowserCodeReader.listVideoInputDevices();
        if (cancelled) return;
        setCameraDevices(devices);
        const deviceId = devices.length > 0 ? devices[0].deviceId : undefined;
        setSelectedDevice(deviceId || "");
        const reader = new BrowserMultiFormatReader();
        readerRef.current = reader;
        startDecoding(reader, deviceId);
      } catch (err) {
        if (!cancelled) setCameraError("Camera not available: " + err.message);
      }
    })();
    return () => {
      cancelled = true;
      stopDecoding();
    };
  }, [cameraModal]);

  useEffect(() => {
    if (!cameraModal || !readerRef.current || !selectedDevice) return;
    stopDecoding();
    startDecoding(readerRef.current, selectedDevice);
  }, [selectedDevice]);

  const startDecoding = async (reader, deviceId) => {
    if (!videoRef.current) return;
    try {
      const controls = await reader.decodeFromVideoDevice(
        deviceId,
        videoRef.current,
        (result) => { if (result) handleBarcodeDetected(result.getText()); }
      );
      controlsRef.current = controls;
    } catch (err) {
      setCameraError("Could not start camera: " + err.message);
    }
  };

  const stopDecoding = () => {
    if (controlsRef.current) {
      try { controlsRef.current.stop(); } catch (_) { }
      controlsRef.current = null;
    }
  };

  const closeCameraModal = () => {
    stopDecoding();
    setCameraModal(false);
    setScanResult("");
    setScanFlash(false);
    setCameraError("");
    setCameraDevices([]);
    setSelectedDevice("");
  };

  // Barcode handler (camera & USB keyboard wedge)
  const handleBarcodeDetected = useCallback(async (code) => {
    if (!code || code.length < 2) return;
    setScanResult(code);
    setScanFlash(true);
    setTimeout(() => setScanFlash(false), 350);
    try {
      const res = await axiosPrivate.get(`/api/products/barcode?code=${encodeURIComponent(code)}`);
      if (res.data) {
        addToCart(res.data);
        toast.success(`✓ ${res.data.name}`, { autoClose: 1500 });
        setTimeout(() => closeCameraModal(), 600);
      } else {
        toast.warn(`No product for barcode: ${code}`);
      }
    } catch {
      toast.error("Barcode lookup failed");
    }
  }, [axiosPrivate]);

  // USB/BT barcode scanner support
  const scanBufferRef = useRef("");
  const scanTimerRef = useRef(null);
  const SCAN_TIMEOUT = 80;
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "Enter") {
        const barcode = scanBufferRef.current.trim();
        scanBufferRef.current = "";
        clearTimeout(scanTimerRef.current);
        if (barcode.length >= 3) handleBarcodeDetected(barcode);
        return;
      }
      if (e.key.length === 1) {
        scanBufferRef.current += e.key;
        clearTimeout(scanTimerRef.current);
        scanTimerRef.current = setTimeout(() => {
          scanBufferRef.current = "";
        }, SCAN_TIMEOUT);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleBarcodeDetected]);

  // ── Category options ───────────────────────────────────
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await axiosPrivate.get("/api/categories");
        setCategories(res.data);
      } catch (err) { console.warn("Failed to load categories", err); }
    })();
  }, [user]);
  const categoryOptions = useMemo(() => ["All Categories", ...categories.map(c => c.name)], [categories]);

  /* inject CSS once */
  useEffect(() => {
    const id = "pos-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id; el.textContent = CSS;
      document.head.appendChild(el);
    }
    return () => { };
  }, []);

  // Full‑screen listener
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen().catch(() => { });
    else document.exitFullscreen();
  };

  // Product search
  useEffect(() => {
    clearTimeout(searchRef.current);
    if (!search.trim()) { setProducts([]); return; }
    setLoading(true);
    searchRef.current = setTimeout(async () => {
      try {
        const res = await axiosPrivate.get(`/api/products/search?q=${encodeURIComponent(search)}`);
        setProducts(res.data);
      } catch { toast.error("Failed to fetch products"); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(searchRef.current);
  }, [search]);

  // Customer search
  const custSearchRef = useRef();
  useEffect(() => {
    clearTimeout(custSearchRef.current);
    if (!customerSearch.trim()) { setCustomers([]); return; }
    setCustomerSearching(true);
    custSearchRef.current = setTimeout(async () => {
      try {
        const res = await axiosPrivate.get(`/api/customers/search?q=${encodeURIComponent(customerSearch)}`);
        setCustomers(res.data);
      } catch { toast.error("Failed to fetch customers"); }
      finally { setCustomerSearching(false); }
    }, 300);
    return () => clearTimeout(custSearchRef.current);
  }, [customerSearch]);

  // Product filtering
  const filtered = useMemo(() => products.filter(p => {
    const matchCat = category === "All Categories" || p.categoryName === category;
    const matchBrand = brand === "All Brands" || p.brand === brand;
    return matchCat && matchBrand;
  }), [products, category, brand]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // addToCart plays beep on every add
  const addToCart = useCallback((product) => {
    playBeep();
    setCart(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) return prev.map(i => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { ...product, qty: 1 }];
    });
  }, [playBeep]);

  const updateQty = (id, delta) => setCart(prev => prev.map(i => i.id === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i));
  const removeItem = (id) => setCart(prev => prev.filter(i => i.id !== id));

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const taxAmt = subtotal * (taxPct / 100);
  const discountAmt = parseFloat(discount) || 0;
  const shippingAmt = parseFloat(shipping) || 0;
  const grand = subtotal + taxAmt - discountAmt + shippingAmt;

  const handleReset = useCallback(() => {
    setCart([]); setTaxPct(0); setDiscount(0); setShipping(0); setPoints(0);
    setSelectedCustomer(null); setCustomerSearch("");
  }, []);

  // ── Drafts handlers ─────────────────────────────────
  const holdCart = useCallback(() => {
    if (cart.length === 0) return;
    const draft = {
      id: Date.now(),
      items: cart,
      customer: selectedCustomer,
      taxPct,
      discount,
      shipping,
      points,
      createdAt: new Date().toISOString(),
    };
    setDrafts(prev => [...prev, draft]);
    handleReset();
    toast.success("Sale held as draft");
  }, [cart, selectedCustomer, taxPct, discount, shipping, points, handleReset]);

  const loadDraft = useCallback((draftId) => {
    const draft = drafts.find(d => d.id === draftId);
    if (!draft) return;
    setCart(draft.items);
    setSelectedCustomer(draft.customer);
    setTaxPct(draft.taxPct);
    setDiscount(draft.discount);
    setShipping(draft.shipping);
    setPoints(draft.points);
    setCustomerSearch("");
    setDrafts(prev => prev.filter(d => d.id !== draftId));
    setDraftsModalVisible(false);
    toast.success("Draft loaded");
  }, [drafts]);

  const deleteDraft = useCallback((draftId) => {
    setDrafts(prev => prev.filter(d => d.id !== draftId));
    toast.info("Draft removed");
  }, []);

  // ── Integrated checkout logic: requires open register ──
  const handleCheckout = async () => {
    if (!cart.length) return;
    if (!hasOpenSession) {
      toast.error("Please open the register before making a sale.");
      return;
    }
    if (!paymentMethod) {
      toast.warn("Select a payment method");
      return;
    }
    if (paymentMethod === "CASH" && (parseFloat(amountTendered) || 0) < grand) {
      toast.warn("Amount received is less than the total due");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axiosPrivate.post("/api/sales", {
        items: cart.map(i => ({ productId: i.id, quantity: i.qty })),
        taxPercent: parseFloat(taxPct) || 0,
        discountAmount: parseFloat(discount) || 0,
        shippingAmount: parseFloat(shipping) || 0,
        customerId: selectedCustomer?.id || null,
        pointsToUse: points || 0,
        paymentMethod: paymentMethod,
        registerSessionId: session?.id,
      });

      const amountPaid = paymentMethod === "CASH"
        ? (parseFloat(amountTendered) || grand)
        : grand;

      setReceiptData({
        reference: res.data?.reference || res.data?.invoiceNumber || `SL-${res.data?.id ?? Date.now()}`,
        date: new Date(),
        seller: user?.firstName || user?.name || username,
        customer: selectedCustomer,
        branch: user?.branch?.name,
        items: cart,
        subtotal, taxAmt, discountAmt, shippingAmt, grand,
        paymentMethod,
        amountPaid,
        change: Math.max(0, amountPaid - grand),
      });

      toast.success("Sale completed successfully");
      handleReset();
      setPayModal(false);
      setPaymentMethod("");
      setAmountTendered("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Sale failed");
    } finally {
      setSubmitting(false);
    }
  };

  // New customer creation
  const handleCreateCustomer = async () => {
    if (!newCustomerForm.name.trim()) { toast.warn("Customer name is required"); return; }
    try {
      const res = await axiosPrivate.post("/api/customers", newCustomerForm);
      setSelectedCustomer(res.data);
      setNewCustomerModal(false);
      setNewCustomerForm({ name: "", phone: "", email: "" });
      toast.success("Customer created");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create customer");
    }
  };

  const username = (() => {
    try {
      const decoded = JSON.parse(atob((localStorage.getItem("accessToken") || "..").split(".")[1]));
      return decoded.username || decoded.sub?.split("@")[0] || "U";
    } catch { return "U"; }
  })();

  return (
    <div className="pos-root">
      {/* ── Topbar ── */}
      <div className="pos-topbar">
        <div className="pos-topbar__brand">S</div>
        <div className="pos-status-dot" />

        {!hasOpenSession ? (
          <button className="pos-register-btn" onClick={() => setOpenModalVisible(true)}>
            Open Register
          </button>
        ) : (
          <button className="pos-register-btn" onClick={() => setCloseModalVisible(true)}
            style={{ borderColor: 'rgba(255,71,87,0.4)', background: 'rgba(255,71,87,0.1)', color: '#ff6b7a' }}>
            Close Register
          </button>
        )}

        <div className="pos-topbar__divider" />
        <DarkSelect value={warehouse} onChange={setWarehouse} options={WAREHOUSES} width={148} />
        <DarkSelect value={category} onChange={v => { setCategory(v); setPage(1); }} options={categoryOptions} width={158} />
        <DarkSelect value={brand} onChange={v => { setBrand(v); setPage(1); }} options={BRANDS} width={138} />
        <div style={{ flex: 1 }} />
        <button className="pos-topbar__icon-btn" aria-label="Fullscreen" onClick={toggleFullscreen}>
          {isFullscreen ? <IconMinimize /> : <IconMaximize />}
        </button>
        <div className="pos-topbar__divider" />
        <div className="pos-topbar__avatar">{username[0].toUpperCase()}</div>
      </div>

      {hasOpenSession && <RegisterDashboard />}
      <WithdrawalBanner />

      <div className="pos-main">
        <div className="pos-checkout">
          <div className="pos-checkout__header">
            Checkout
            {cart.length > 0 && (
              <span className="pos-checkout__count">{cart.length} item{cart.length !== 1 ? "s" : ""}</span>
            )}
          </div>

          {!hasOpenSession && (
            <div style={{
              background: "rgba(255,71,87,0.1)", border: "1px solid rgba(255,71,87,0.3)",
              borderRadius: 8, padding: "6px 12px", margin: "8px 16px 0",
              color: "#ff6b7a", fontSize: 11, display: "flex", alignItems: "center", gap: 6
            }}>
              <IconPause /> Register closed – sales are blocked
            </div>
          )}

          {/* ── Customer Section ── */}
          <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <label className="pos-field-label">CUSTOMER</label>
            {selectedCustomer ? (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.3)",
                borderRadius: 8, padding: "8px 10px"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: 12, flexShrink: 0
                  }}>
                    {selectedCustomer.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 12, color: "#e2e4f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {selectedCustomer.name}
                    </div>
                    <div style={{ fontSize: 10, color: "#8b8ff7" }}>
                      {selectedCustomer.points || 0} pts
                    </div>
                  </div>
                </div>
                <button onClick={() => { setSelectedCustomer(null); setCustomerSearch(""); setPoints(0); }}
                  style={{ border: "none", background: "transparent", color: "rgba(255,255,255,0.4)", fontSize: 18, cursor: "pointer", padding: "0 4px" }}>
                  ×
                </button>
              </div>
            ) : (
              <div style={{ position: "relative" }} ref={customerRef}>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <div className="pos-search-wrap" style={{ flex: 1 }}>
                    <span className="pos-search-icon"><IconSearch /></span>
                    <input
                      value={customerSearch}
                      onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                      onFocus={() => setShowCustomerDropdown(true)}
                      placeholder="Search customer..."
                      className="pos-search-input"
                    />
                    {customerSearching && <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>...</span>}
                  </div>
                  <button onClick={() => setNewCustomerModal(true)} title="New customer"
                    className="pos-scan-btn" style={{ width: 30, height: 30 }}>
                    <IconPlus />
                  </button>
                </div>
                {showCustomerDropdown && customers.length > 0 && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
                    background: "#1a1d2e", border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                    maxHeight: 160, overflowY: "auto", marginTop: 4
                  }}>
                    {customers.map(c => (
                      <div key={c.id} onClick={() => { setSelectedCustomer(c); setCustomerSearch(""); setShowCustomerDropdown(false); }}
                        style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: 12 }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.1)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ fontWeight: 500, color: "#e2e4f0" }}>{c.name}</div>
                        <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 10 }}>{c.phone || "—"} · {c.points || 0} pts</div>
                      </div>
                    ))}
                  </div>
                )}
                {showCustomerDropdown && customers.length === 0 && customerSearch.trim() && !customerSearching && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
                    background: "#1a1d2e", border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 8, padding: 12, textAlign: "center", fontSize: 11, color: "rgba(255,255,255,0.4)"
                  }}>
                    No customers found.
                    <span onClick={() => { setNewCustomerModal(true); setShowCustomerDropdown(false); }}
                      style={{ color: "#8b8ff7", cursor: "pointer", marginLeft: 4 }}>Create new?</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Cart items ── */}
          <div className="pos-cart">
            {cart.length === 0 ? (
              <div className="pos-cart__empty">
                <IconCart />
                <span className="pos-cart__empty-label">No items added</span>
                <span className="pos-cart__empty-sub">Search and select products</span>
              </div>
            ) : cart.map(item => (
              <div key={item.id} className="pos-cart__item">
                <img src={item.avatarUrl || "https://via.placeholder.com/40"} alt={item.name} className="pos-cart__img" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="pos-cart__name">{item.name}</div>
                  <div className="pos-cart__price">{fmt(item.price)}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <button className="pos-qty-btn" onClick={() => updateQty(item.id, -1)}><IconMinus /></button>
                  <span className="pos-qty-val">{item.qty}</span>
                  <button className="pos-qty-btn" onClick={() => updateQty(item.id, 1)}><IconPlus /></button>
                </div>
                <div className="pos-cart__total">{fmt(item.price * item.qty)}</div>
                <button className="pos-remove-btn" onClick={() => removeItem(item.id)}>×</button>
              </div>
            ))}
          </div>

          {/* ── Inputs ── */}
          <div className="pos-inputs">
            <div className="pos-inputs__row">
              {[
                { label: "TAX", value: taxPct, setter: setTaxPct, suffix: "%" },
                { label: "DISCOUNT", value: discount, setter: setDiscount, suffix: "TND" },
                { label: "SHIPPING", value: shipping, setter: setShipping, suffix: "TND" },
              ].map(({ label, value, setter, suffix }) => (
                <div key={label} style={{ flex: 1 }}>
                  <label className="pos-field-label">{label}</label>
                  <div className="pos-field-wrap">
                    <input type="number" min="0" value={value} onChange={e => setter(e.target.value)} className="pos-field-input" />
                    <span className="pos-field-suffix">{suffix}</span>
                  </div>
                </div>
              ))}
            </div>

            {selectedCustomer && (
              <>
                <div className="pos-points-bar">
                  <span className="pos-points-label">Available Points</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#e2e4f0" }}>{selectedCustomer.points || 0}</span>
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <div className="pos-field-wrap" style={{ flex: 1 }}>
                    <input type="number" min="0" max={selectedCustomer.points || 0}
                      value={points} onChange={e => setPoints(e.target.value)}
                      placeholder="0" className="pos-field-input" />
                  </div>
                  <button className="pos-convert-btn">Use Points</button>
                </div>
              </>
            )}
          </div>

          {/* ── Totals ── */}
          <div className="pos-totals">
            {[["SUBTOTAL", fmt(subtotal)], ["TAX", fmt(taxAmt)], ["DISCOUNT", `-${fmt(discountAmt)}`], ["SHIPPING", fmt(shippingAmt)]].map(([label, val]) => (
              <div key={label} className="pos-totals__row">
                <span className="pos-totals__label">{label}</span>
                <span className={`pos-totals__val${label === "DISCOUNT" ? "--discount" : ""}`}>{val}</span>
              </div>
            ))}
            <div className="pos-totals__grand">
              <span className="pos-totals__grand-label">GRAND TOTAL</span>
              <span className="pos-totals__grand-val">{fmt(grand)}</span>
            </div>
          </div>
        </div>

        {/* ── Products Panel ── */}
        <div className="pos-products">
          <div className="pos-search-bar">
            <div className="pos-search-wrap">
              <span className="pos-search-icon"><IconSearch /></span>
              <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Scan or search product by code or name…" className="pos-search-input" />
            </div>
            <button
              className={`pos-scan-btn${cameraModal ? " pos-scan-btn--active" : ""}`}
              aria-label="Camera barcode scan"
              title="Scan barcode with camera"
              onClick={() => cameraModal ? closeCameraModal() : setCameraModal(true)}
            >
              <IconBarcode />
            </button>
          </div>
          <div className="pos-grid-wrap">
            {loading ? <div className="pos-empty">Loading…</div> :
              products.length === 0 ? <div className="pos-empty">Type to search for products</div> :
                <>
                  <div className="pos-grid">
                    {paginated.map(product => (
                      <div key={product.id} className="pos-card" onClick={() => addToCart(product)}>
                        <div className="pos-card__img-wrap">
                          <img src={product.avatarUrl || "https://via.placeholder.com/150"} alt={product.name} className="pos-card__img" />
                        </div>
                        <div className="pos-card__body">
                          <div className="pos-card__name">{product.name}</div>
                          <div className="pos-card__sku">{product.sku}</div>
                          <div className="pos-card__footer">
                            <span className="pos-card__price">{fmt(product.price)}</span>
                            <button className="pos-add-btn" onClick={e => { e.stopPropagation(); addToCart(product); }}>+</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <div className="pos-pagination">
                      <button className="pos-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                        <button key={n} className={`pos-page-btn${n === page ? " pos-page-btn--active" : ""}`} onClick={() => setPage(n)}>{n}</button>
                      ))}
                      <button className="pos-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                      <span className="pos-page-info">{filtered.length} products</span>
                    </div>
                  )}
                </>
            }
          </div>
        </div>
      </div>

      {/* ── Bottom bar (updated with Hold & Recent Drafts) ── */}
      <div className="pos-bottombar">
        <div className="pos-online"><span className="pos-online__dot" /><span className="pos-online__label">Online</span></div>
        <button className="pos-bottom-btn" onClick={handleReset}>
          <IconReset /> RESET
        </button>
        <button
          className="pos-bottom-btn"
          onClick={() => setDraftsModalVisible(true)}
          style={{ position: "relative" }}
        >
          <IconFile /> RECENT DRAFTS
          {drafts.length > 0 && (
            <span style={{
              position: "absolute", top: 2, right: 2,
              background: "#6366f1", color: "#fff",
              borderRadius: "50%", fontSize: 10, fontWeight: 700,
              width: 18, height: 18, display: "flex",
              alignItems: "center", justifyContent: "center",
            }}>
              {drafts.length}
            </span>
          )}
        </button>
        <button
          className="pos-bottom-btn"
          onClick={holdCart}
          disabled={cart.length === 0}
          title={cart.length === 0 ? "Add items first" : "Hold current sale"}
        >
          <IconPause /> HOLD
        </button>
        <div style={{ flex: 1 }} />
        <div className="pos-total-block">
          <div className="pos-total-block__label">Total Payable</div>
          <div className="pos-total-block__amount">{fmt(grand)}</div>
        </div>
        <button
          className="pos-pay-btn"
          disabled={cart.length === 0 || !hasOpenSession}
          onClick={() => cart.length > 0 && hasOpenSession && setPayModal(true)}
          title={!hasOpenSession ? "Register is closed" : ""}
        >
          <IconCheck /> PAY NOW
        </button>
      </div>

      {/* ── Pay modal ── */}
      {payModal && (
        <div className="pos-modal-overlay">
          <div className="pos-modal">
            <h3>Complete payment</h3>
            <div className="pos-modal__summary">
              <div className="pos-modal__summary-row">
                <span className="pos-modal__summary-label">Grand total</span>
                <span className="pos-modal__summary-val">{fmt(grand)}</span>
              </div>
              <div className="pos-modal__summary-row">
                <span className="pos-modal__summary-label">Items</span>
                <span className="pos-modal__summary-val--neutral">
                  {cart.reduce((s, i) => s + i.qty, 0)}
                </span>
              </div>
              {selectedCustomer && (
                <div className="pos-modal__summary-row">
                  <span className="pos-modal__summary-label">Customer</span>
                  <span className="pos-modal__summary-val--neutral">
                    {selectedCustomer.name}
                  </span>
                </div>
              )}
            </div>

            <div className="pos-method-grid">
              {["CASH", "CARD", "MOBILE", "Points"].map(method => (
                <button
                  key={method}
                  className="pos-method-btn"
                  onClick={() => setPaymentMethod(method)}
                  style={{
                    borderColor: paymentMethod === method ? '#6366f1' : undefined,
                    background: paymentMethod === method ? 'rgba(99,102,241,0.15)' : undefined,
                    color: paymentMethod === method ? '#fff' : undefined,
                  }}
                >
                  {method}
                </button>
              ))}
            </div>

            {paymentMethod === "CASH" && (
              <div style={{ marginBottom: 16 }}>
                <label className="pos-field-label">Amount Received</label>
                <div className="pos-field-wrap">
                  <input
                    type="number" min="0" step="0.001"
                    value={amountTendered}
                    onChange={e => setAmountTendered(e.target.value)}
                    placeholder={grand.toFixed(3)}
                    className="pos-field-input"
                  />
                  <span className="pos-field-suffix">TND</span>
                </div>
                {amountTendered !== "" && (
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>
                    Change: {fmt(Math.max(0, (parseFloat(amountTendered) || 0) - grand))}
                  </div>
                )}
              </div>
            )}

            <div className="pos-modal__actions">
              <button
                className="pos-modal__cancel"
                onClick={() => {
                  setPayModal(false);
                  setPaymentMethod("");
                  setAmountTendered("");
                }}
              >
                Cancel
              </button>
              <button
                className="pos-modal__confirm"
                disabled={submitting || !paymentMethod}
                onClick={handleCheckout}
              >
                {submitting ? "Processing…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── New Customer Modal ── */}
      {newCustomerModal && (
        <div className="pos-modal-overlay">
          <div className="pos-modal">
            <h3>New Customer</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
              <div>
                <label className="pos-field-label">Name *</label>
                <div className="pos-field-wrap">
                  <input type="text" value={newCustomerForm.name} onChange={e => setNewCustomerForm({ ...newCustomerForm, name: e.target.value })}
                    placeholder="Customer name" className="pos-field-input" />
                </div>
              </div>
              <div>
                <label className="pos-field-label">Phone</label>
                <div className="pos-field-wrap">
                  <input type="text" value={newCustomerForm.phone} onChange={e => setNewCustomerForm({ ...newCustomerForm, phone: e.target.value })}
                    placeholder="Phone number" className="pos-field-input" />
                </div>
              </div>
              <div>
                <label className="pos-field-label">Email</label>
                <div className="pos-field-wrap">
                  <input type="email" value={newCustomerForm.email} onChange={e => setNewCustomerForm({ ...newCustomerForm, email: e.target.value })}
                    placeholder="Email address" className="pos-field-input" />
                </div>
              </div>
            </div>
            <div className="pos-modal__actions">
              <button className="pos-modal__cancel" onClick={() => setNewCustomerModal(false)}>Cancel</button>
              <button className="pos-modal__confirm" onClick={handleCreateCustomer}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Camera Barcode Scanner Modal ── */}
      {cameraModal && (
        <div className="pos-modal-overlay" onClick={e => e.target === e.currentTarget && closeCameraModal()}>
          <div className="pos-camera-modal">
            <div className="pos-camera-modal__header">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                  <IconCamera />
                </div>
                <span className="pos-camera-modal__title">Scan Barcode</span>
              </div>
              <button className="pos-camera-modal__close" onClick={closeCameraModal}><IconX /></button>
            </div>
            {cameraDevices.length > 1 && (
              <div style={{ padding: "0 20px 10px" }}>
                <select
                  className="pos-camera-select"
                  value={selectedDevice}
                  onChange={e => setSelectedDevice(e.target.value)}
                >
                  {cameraDevices.map((d, i) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Camera ${i + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="pos-camera-viewport">
              {cameraError ? (
                <div style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "center", height: "100%", gap: 12, padding: 24,
                  color: "rgba(255,255,255,0.4)", textAlign: "center", fontSize: 13
                }}>
                  <IconCamera />
                  <span>{cameraError}</span>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.25)" }}>
                    Make sure camera permissions are granted.
                  </span>
                </div>
              ) : (
                <>
                  <video ref={videoRef} autoPlay muted playsInline />
                  {scanFlash && <div className="pos-scan-success-flash" />}
                  <div className="pos-scan-overlay">
                    <div className="pos-scan-frame">
                      <div className="pos-scan-frame__inner" />
                      <div className="pos-scan-laser" />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="pos-camera-modal__footer">
              <div className="pos-camera-status">
                <span className={`pos-camera-status__dot${cameraError ? " pos-camera-status__dot--idle" : ""}`} />
                {cameraError ? "Camera unavailable" : scanResult ? `Last scan: ${scanResult}` : "Scanning — hold barcode inside the frame"}
              </div>
              {scanResult && (
                <div className="pos-camera-result">
                  <IconBarcode />
                  <span className="pos-camera-result__code">{scanResult}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Receipt / Invoice Modal ── */}
      {receiptData && (
        <div className="pos-modal-overlay">
          <div className="pos-modal pos-receipt-modal">
            <div className="pos-receipt-printable">
              <div className="pos-receipt-header">
                <div className="pos-receipt-logo">S</div>
                <div className="pos-receipt-brand">StockPOS</div>
              </div>
              <div className="pos-receipt-meta">
                <div>Reference: {receiptData.reference}</div>
                <div>Date: {receiptData.date.toLocaleString()}</div>
                <div>Seller: {receiptData.seller}</div>
                <div>Customer: {receiptData.customer?.name || "Walk-In Customer"}</div>
                <div>Branch: {receiptData.branch}</div>
              </div>
              <div className="pos-receipt-items">
                {receiptData.items.map(item => (
                  <div key={item.id} className="pos-receipt-item-row">
                    <span className="pos-receipt-item-name">{item.name}</span>
                    <span className="pos-receipt-item-qty">{item.qty} × {fmt(item.price)}</span>
                    <span className="pos-receipt-item-total">{fmt(item.qty * item.price)}</span>
                  </div>
                ))}
              </div>
              <div className="pos-receipt-totals">
                <div><span>Subtotal</span><span>{fmt(receiptData.subtotal)}</span></div>
                <div><span>Tax</span><span>{fmt(receiptData.taxAmt)}</span></div>
                <div><span>Discount</span><span>-{fmt(receiptData.discountAmt)}</span></div>
                <div><span>Shipping</span><span>{fmt(receiptData.shippingAmt)}</span></div>
                <div className="pos-receipt-total-grand"><span>Total</span><span>{fmt(receiptData.grand)}</span></div>
                <div><span>Paid By</span><span>{receiptData.paymentMethod}</span></div>
                <div><span>Amount</span><span>{fmt(receiptData.amountPaid)}</span></div>
                <div><span>Change</span><span>{fmt(receiptData.change)}</span></div>
              </div>
              <div className="pos-receipt-thanks">Thank you for shopping with us.<br />Please come again!</div>
              <svg ref={barcodeRef} className="pos-receipt-barcode" />
            </div>
            <div className="pos-modal__actions pos-receipt-noprint">
              <button className="pos-modal__cancel" onClick={() => setReceiptData(null)}>Close</button>
              <button className="pos-modal__confirm" onClick={() => window.print()}>
                <IconPrinter /> Print
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Drafts Modal ── */}
      {draftsModalVisible && (
        <div className="pos-modal-overlay" onClick={e => e.target === e.currentTarget && setDraftsModalVisible(false)}>
          <div className="pos-modal pos-drafts-modal">
            <h3>Recent Drafts</h3>
            {drafts.length === 0 ? (
              <div style={{ textAlign: "center", padding: 30, color: "rgba(255,255,255,0.4)" }}>
                No saved drafts
              </div>
            ) : (
              <div className="pos-drafts-list">
                {drafts.map(draft => {
                  const itemCount = draft.items.reduce((s, i) => s + i.qty, 0);
                  const total = draft.items.reduce((s, i) => s + i.price * i.qty, 0);
                  const date = new Date(draft.createdAt);
                  return (
                    <div key={draft.id} className="pos-draft-card">
                      <div className="pos-draft-card__info">
                        <div className="pos-draft-card__meta">
                          {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                        <div className="pos-draft-card__items">
                          {itemCount} item{itemCount !== 1 ? "s" : ""} · Total: {fmt(total)}
                        </div>
                        {draft.customer && (
                          <div className="pos-draft-card__customer">
                            {draft.customer.name}
                          </div>
                        )}
                      </div>
                      <div className="pos-draft-card__actions">
                        <button className="pos-modal__confirm" onClick={() => loadDraft(draft.id)}>
                          Load
                        </button>
                        <button className="pos-modal__cancel" onClick={() => deleteDraft(draft.id)}>
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="pos-modal__actions" style={{ marginTop: 16 }}>
              <button className="pos-modal__cancel" onClick={() => setDraftsModalVisible(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Register Modals ──────────────────────────────── */}
      <OpenRegisterModal />
      <CloseRegisterModal />
      <WithdrawalModal />
    </div>
  );
}

/* ── Dark Select ──────────────────────────────────────────── */
function DarkSelect({ value, onChange, options, width }) {
  return (
    <div className="pos-select-wrap" style={{ width }}>
      <select className="pos-select" style={{ width: "100%" }} value={value} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
      <span className="pos-select-chevron"><IconChevron /></span>
    </div>
  );
}